import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { AboutButton, RightControllerIllustration } from '@versports/ui'
import { PLAYER_SETUP_STEPS as DONE_STEPS } from '../setup/playerSetupSteps'
import { useAuth } from '../auth/AuthContext'
import type { MeResponse } from '../api/auth'
import type { KitGates } from '../api/kitAuth'
import { type KitAccess, type KitAccessClub } from '../kit/kitAccessStore'
import { useKitBinding, type KitBindingStatus } from '../kit/useKitBinding'
import { useIdleLogout } from './useIdleLogout'

/**
 * MODO TABLET (`/tablet`) — la tablet compartida que viene adentro del kit del
 * club. No es el flujo personal: acá pasan MUCHOS jugadores por el mismo
 * dispositivo, uno atrás del otro, antes de ponerse el casco.
 *
 * La ruta era `/kit` hasta el 2026-09-07; el nombre confundía (mg: *"estaba
 * /kits pero creo que eso está mal"*) porque el kit no es la pantalla, es la
 * credencial del dispositivo. El redirect de compatibilidad `/kit` → `/tablet`
 * se sacó el mismo día; `/kit` cae directo en el 404 general.
 *
 * Ciclo de una sesión (todo el estado vive en este layout, así que la
 * pantalla de PIN queda lista para el siguiente sin recargar nada):
 *
 *   code → pin → confirm → [consent] → [profile] → [questionnaire] → checkin → thanks|uptodate → logout → pin
 *
 *   - `code`     PASO 1, la autenticación del DISPOSITIVO con el club: se pide
 *                cuando la tablet no está ligada a ningún kit, y se vuelve a
 *                pedir cuando el binding deja de servir (kit revocado, club
 *                con el servicio pausado, storage borrado). La tabla de
 *                estados vive en `kit/useKitBinding.ts`, compartida con el
 *                visor.
 *   - `pin`      6 dígitos, el único identificador del jugador.
 *   - `confirm`  "Hola {display_name} — ¿sos vos?". Un typo que caiga en el
 *                PIN de un compañero loguearía al jugador equivocado en
 *                silencio, y en un estudio eso es un trial mal atribuido.
 *   - `consent`  si `gates.consent_required` (firma antes del cuestionario).
 *   - ruta hija `/tablet/data/:code` si `gates.questionnaire_pending`: es el
 *     MISMO `KnowQuestionnaire` del flujo personal, montado adentro de este
 *     layout en vez de en `/data/:code` para que el auto-logout y el
 *     watchdog de inactividad sigan vivos mientras el jugador responde (en
 *     `/data/*` el árbol del modo tablet se desmontaría y la tablet quedaría
 *     con la sesión abierta del jugador anterior).
 *
 * `/tablet?resume=1` entra por un atajo: lo linkea el paso "Cuestionario
 * pendiente" del visor (`vr-app/src/ui/lobby/QuestionnaireGateStep.tsx`)
 * cuando el jugador está usando la tablet. El login por kit ya dejó la cookie
 * de sesión en este browser, así que con `/auth/me` se rearma la sesión de
 * tablet (`sessionFromMe`) y se saltea el PIN y el `confirm`: la identidad se
 * confirmó cuando tecleó los 6 dígitos. Si no hay sesión, cae al PIN sin
 * decir nada.
 *
 * El logout entre jugadores limpia la sesión de auth y NUNCA el kitAccess.
 */

const IDLE_TIMEOUT_MS = 4 * 60 * 1000

export type TabletPhase = 'code' | 'pin' | 'confirm' | 'consent' | 'profile' | 'questionnaire' | 'checkin' | 'uptodate' | 'thanks'

export interface TabletSession {
  displayName: string
  gates: KitGates
  profilePending: string[]
  checkInDone: boolean
  afterQuestions: boolean
}

/**
 * Superset de `KnowOutletCtx` (pages/data/KnowLayout.tsx): la ruta hija
 * `/tablet/data/:code` renderiza `KnowQuestionnaire`, que consume ese contexto.
 * `exitPath` hace que su botón "Salir" vuelva a `/tablet` y no a `/data`, que
 * en una tablet compartida sería una puerta al área personal del jugador.
 */
export interface TabletOutletCtx {
  revalidate: () => Promise<void>
  phase: TabletPhase
  locationKey: string
  setFullscreen: (fullscreen: boolean) => void
  exitPath: string
  access: KitAccess | null
  /** Estado del binding del dispositivo — ver `kit/useKitBinding.ts`. */
  bindingStatus: KitBindingStatus
  /** Por qué se volvió a pedir el código, o null en un arranque limpio. */
  bindingReason: string | null
  /** Desligar la tablet del kit (staff). */
  unbindDevice: () => void
  session: TabletSession | null
  setAccess: (access: KitAccess) => void
  onLoggedIn: (session: TabletSession) => void
  onIdentityConfirmed: () => void
  onConsentDone: () => void
  onProfileDone: () => void
  onCheckInDone: () => void
  endSession: () => void
}

export function useTabletContext(): TabletOutletCtx {
  return useOutletContext<TabletOutletCtx>()
}

export function TabletLayout() {
  const { logout, refresh } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Paso 1: el binding del dispositivo con el club. `bindingStatus` decide si
  // esta tablet puede ofrecer el PIN; `bindingReason` es lo que se le explica
  // al staff cuando no puede.
  const {
    status: bindingStatus,
    access,
    reason: bindingReason,
    bind,
    unbind,
  } = useKitBinding()
  const bound = bindingStatus === 'bound'

  const [session, setSession] = useState<TabletSession | null>(null)
  const [phase, setPhase] = useState<TabletPhase>(bound ? 'pin' : 'code')
  const [epoch, setEpoch] = useState(0)
  const [closing, setClosing] = useState(false)
  const [closeError, setCloseError] = useState(false)
  const closingRef = useRef(false)

  // El re-verify de arranque es asincrónico: si vuelve con el kit revocado o
  // el club pausado mientras la tablet ya mostraba el PIN, hay que sacarla de
  // ahí (y al revés, dejarla entrar cuando el binding se resuelve bien).
  useEffect(() => {
    setPhase((prev) => {
      if (!bound) return 'code'
      return prev === 'code' ? 'pin' : prev
    })
  }, [bound])

  // El submit del cuestionario no expone callback: `KnowQuestionnaire`
  // señaliza el éxito estampando `state.justSubmitted` en la entry de
  // history (lo hace así para sobrevivir al remount que dispara su
  // `refresh()`). Leerlo acá es el enganche del auto-logout post-envío.
  const justSubmitted = Boolean((location.state as { justSubmitted?: boolean } | null)?.justSubmitted)

  const boundRef = useRef(bound)
  boundRef.current = bound

  const endSession = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setClosing(true)
    setCloseError(false)
    // Do not accept the next PIN while an old logout can still clear its cookie.
    void (async () => {
      try {
        await logout()
        setSession(null)
        setChaining(false)
        setChainError(false)
        setPhase(boundRef.current ? 'pin' : 'code')
        setEpoch((e) => e + 1)
        navigate('/tablet', { replace: true })
        setClosing(false)
      } catch {
        setCloseError(true)
      } finally {
        closingRef.current = false
      }
    })()
  }, [logout, navigate])

  const setAccess = useCallback(
    (next: KitAccess) => {
      bind(next)
      setPhase('pin')
    },
    [bind],
  )

  // Desligar la tablet del kit (acción de STAFF, no logout entre jugadores):
  // vuelve al paso 1 sin recargar la página.
  const unbindDevice = useCallback(() => {
    setSession(null)
    unbind()
    setPhase('code')
    setEpoch((e) => e + 1)
  }, [unbind])

  const onLoggedIn = useCallback((next: TabletSession) => {
    setSession(next)
    setPhase('confirm')
  }, [])

  const advanceAfterGates = useCallback(
    (next: TabletSession) => {
      // Persist and route from the same snapshot; React state updates are asynchronous.
      setSession(next)
      if (next.gates.consent_required) {
        setPhase('consent')
        navigate('/tablet', { replace: true })
        return
      }
      if (next.profilePending.length > 0) {
        setPhase('profile')
        navigate('/tablet/profile')
        return
      }
      const pending = next.gates.questionnaire_pending
      if (pending) {
        setPhase('questionnaire')
        navigate(`/tablet/data/${encodeURIComponent(pending.code)}`)
        return
      }
      if (!next.checkInDone) {
        setPhase('checkin')
        navigate('/tablet/checkin')
        return
      }
      setPhase(next.afterQuestions ? 'thanks' : 'uptodate')
      navigate('/tablet', { replace: true })
    },
    [navigate],
  )

  const onIdentityConfirmed = useCallback(() => {
    if (!session) return
    advanceAfterGates(session)
  }, [session, advanceAfterGates])

  const onConsentDone = useCallback(() => {
    if (!session) return
    advanceAfterGates({ ...session, gates: { ...session.gates, consent_required: false } })
  }, [session, advanceAfterGates])

  const onProfileDone = useCallback(() => {
    if (!session) return
    advanceAfterGates({ ...session, profilePending: [] })
  }, [session, advanceAfterGates])

  const onCheckInDone = useCallback(() => {
    if (!session) return
    advanceAfterGates({ ...session, checkInDone: true })
  }, [session, advanceAfterGates])

  // Encadenado de pendientes. `gates.questionnaire_pending` es UNO solo (el
  // más viejo, ver api/kitAuth.ts) y se captura en el login, así que después
  // de un envío está siempre desactualizado: por eso acá se vuelve a pedir
  // `/auth/me`, que sí trae la lista entera (`pending_questionnaires`).
  //
  // Sin esto (bug del checklist 45, 2026-09-07) un jugador con SMTQ +
  // CSAI-2R T1 mandaba el primero, caía en "¡Gracias!" y volvía al PIN: se
  // iba convencido de que había terminado con uno todavía sin responder.
  const [chaining, setChaining] = useState(false)
  const [chainError, setChainError] = useState(false)
  const [checkAttempt, setCheckAttempt] = useState(0)
  useEffect(() => {
    if (closing || !session || phase !== 'questionnaire' || !justSubmitted) return
    let cancelled = false
    // El código recién enviado, de la propia ruta hija `/tablet/data/:code`.
    // `/auth/me` puede tardar en dejar de listarlo (el submit y el refresh no
    // comparten transacción), así que se excluye explícitamente en vez de
    // confiar en que ya desapareció.
    const justDone = decodeURIComponent(location.pathname.split('/').pop() ?? '')
    setChaining(true)
    setChainError(false)
    void (async () => {
      let next: KitGates['questionnaire_pending'] = null
      try {
        const me = await refresh()
        if (!me) throw new Error('Session could not be verified')
        next = nextPending(me, justDone)
      } catch {
        if (!cancelled) {
          setChaining(false)
          setChainError(true)
        }
        return
      }
      if (cancelled) return
      setChaining(false)
      advanceAfterGates({
        ...session,
        afterQuestions: true,
        gates: { ...session.gates, questionnaire_pending: next },
      })
    })()
    return () => {
      cancelled = true
    }
  }, [phase, justSubmitted, location.pathname, refresh, checkAttempt, closing, session, advanceAfterGates])

  // Atajo `?resume=1` desde el visor: una sola vez por montaje (el ref), y
  // sólo si el dispositivo está ligado al kit y no hay sesión abierta. Si
  // `/auth/me` no devuelve un jugador, la tablet sigue como si nada: el PIN.
  const [resuming, setResuming] = useState(false)
  const resumeTried = useRef(false)
  const resumeRequested = new URLSearchParams(location.search).get('resume') === '1'
  useEffect(() => {
    if (!resumeRequested || !bound || phase !== 'pin' || session || resumeTried.current) return
    resumeTried.current = true
    setResuming(true)
    void (async () => {
      let next: TabletSession | null = null
      try {
        const me = await refresh()
        next = me ? sessionFromMe(me) : null
      } catch {
        next = null
      }
      setResuming(false)
      if (next) {
        advanceAfterGates(next)
        return
      }
      // Sacar el `?resume=1` para que un reload no vuelva a intentarlo.
      navigate('/tablet', { replace: true })
    })()
  }, [resumeRequested, bound, phase, session, refresh, advanceAfterGates, navigate])

  // Fallback stale: si el jugador se va a la mitad, la tablet vuelve sola al
  // PIN. Corre en TODAS las pantallas del modo tablet — en `pin` no hay
  // sesión que cerrar, pero sí un PIN a medio tipear que no debería quedar
  // esperando al próximo. `thanks`/`uptodate` ya no tienen reloj propio: se
  // cierran con el botón de la guía, y si el jugador se va sin tocarlo, este
  // watchdog es el único que trae la tablet de vuelta.
  useIdleLogout(IDLE_TIMEOUT_MS, true, endSession)

  const revalidate = useCallback(async () => {}, [])
  const [fullscreen, setFullscreen] = useState(false)
  const setFullscreenStable = useCallback((v: boolean) => setFullscreen(v), [])

  const ctx = useMemo<TabletOutletCtx>(
    () => ({
      revalidate,
      phase,
      locationKey: location.key,
      setFullscreen: setFullscreenStable,
      exitPath: '/tablet',
      access,
      bindingStatus,
      bindingReason,
      unbindDevice,
      session,
      setAccess,
      onLoggedIn,
      onIdentityConfirmed,
      onConsentDone,
      onProfileDone,
      onCheckInDone,
      endSession,
    }),
    [
      revalidate,
      phase,
      location.key,
      setFullscreenStable,
      access,
      bindingStatus,
      bindingReason,
      unbindDevice,
      session,
      setAccess,
      onLoggedIn,
      onIdentityConfirmed,
      onConsentDone,
      onProfileDone,
      onCheckInDone,
      endSession,
    ],
  )

  // Branding del club sobre el subárbol del kit (mismo patrón que ClubPage):
  // las vars viven en este contenedor, no en :root, así que no pelean con el
  // efecto de branding del AuthContext.
  const clubStyle = useMemo(() => clubVars(access?.club ?? null), [access?.club])

  // La guía de cierre es una columna de pasos con ilustración: en el ancho
  // del resto de las pantallas (pensadas para un pad de 6 dígitos) quedaría
  // angosta y larga.
  const wide = phase === 'thanks' || phase === 'uptodate'

  return (
    <KitFrame style={clubStyle} chrome={!fullscreen} wide={wide}>
      {(phase === 'code' || phase === 'pin') && <p className="mb-lg text-center text-sm">
        <a href="/guia" target="_blank" rel="noopener noreferrer" className="underline">Cómo prepararse: abrir la guía en otra pestaña</a>
      </p>}
      {closing ? (
        <div className="panel p-2xl text-center" role="status">
          <h1 className="text-2xl">{closeError ? 'No pudimos cerrar tu sesión' : 'Cerrando tu sesión…'}</h1>
          {closeError && <>
            <p className="text-lg">Revisá la conexión y reintentá antes de pasarle la tablet al próximo jugador.</p>
            <button className="btn-primary mt-lg" onClick={endSession}>Reintentar cierre</button>
          </>}
        </div>
      ) : resuming ? (
        <StatusScreen title="Recuperando tu sesión…" detail="Seguís con la sesión que abriste con tu PIN." />
      ) : chainError && phase === 'questionnaire' ? (
        <div className="panel p-2xl text-center" role="alert">
          <h1 className="text-2xl">Tus respuestas se guardaron</h1>
          <p className="text-lg">No pudimos verificar si te queda otro cuestionario. Revisá la conexión y reintentá antes de ir al visor.</p>
          <button className="btn-primary mt-lg" onClick={() => setCheckAttempt((n) => n + 1)}>Verificar pendientes</button>
          <button className="btn mt-lg" onClick={endSession}>Salir</button>
        </div>
      ) : phase === 'thanks' ? (
        <ThanksScreen displayName={session?.displayName ?? null} onDone={endSession} />
      ) : chaining ? (
        <ChainingScreen />
      ) : (
        <Outlet key={epoch} context={ctx} />
      )}
    </KitFrame>
  )
}

/**
 * El siguiente cuestionario pendiente según `/auth/me`, excluido el que se
 * acaba de enviar. Pura, para poder testear el encadenado sin router.
 */
export function nextPending(
  me: MeResponse | null,
  justDone: string,
): KitGates['questionnaire_pending'] {
  const first = (me?.pending_questionnaires ?? []).find((q) => q.code !== justDone)
  return first ? { code: first.code, title: first.name_es } : null
}

/**
 * La sesión de tablet reconstruida desde `/auth/me`, para el atajo
 * `?resume=1`. Pura, para testearla sin router.
 *
 * Devuelve null cuando la cookie no es de un jugador con turno en la tablet:
 * superadmin o staff de club (entran a `/admin` y `/club`, no a la tablet) y
 * cuenta sin `player_profile`. `checkInDone`/`afterQuestions` arrancan en
 * false igual que en el login por PIN: el check-in es de este turno y todavía
 * no se respondió nada acá.
 */
export function sessionFromMe(me: MeResponse): TabletSession | null {
  if (me.is_superuser || me.club_memberships.length > 0 || !me.profile) return null
  return {
    displayName: me.profile.display_name || me.username,
    gates: {
      consent_required: !me.consent_ok,
      questionnaire_pending: nextPending(me, ''),
    },
    profilePending: me.pending_profile_fields,
    checkInDone: false,
    afterQuestions: false,
  }
}

function clubVars(club: KitAccessClub | null): CSSProperties {
  // Mismo default que ClubPage: el primary de la palette Stage.
  const primary = club?.primaryColor ?? '#E25A1B'
  return {
    ['--club-primary' as string]: primary,
    ['--club-secondary' as string]: club?.secondaryColor ?? primary,
    ['--club-accent' as string]: club?.accentColor ?? primary,
  }
}

/**
 * Chrome del modo tablet: contenido centrado y grande (dedos, no mouse) sobre
 * el degradé del club, más el botón "Acerca de" global obligatorio (regla de
 * CLAUDE.md). Sin Topbar ni AppNavBar: en un dispositivo compartido no hay
 * navegación personal que ofrecer.
 *
 * Tampoco hay barra de título: la tenía (escudo + nombre del club + etiqueta
 * "Tablet del club") y mg la sacó el 2026-09-10. Sin club vinculado no decía
 * nada — el escudo era un "?" y el título repetía palabra por palabra la
 * etiqueta de abajo — y con club vinculado el color de marca ya viene por el
 * degradé y por `--club-primary`. Qué dispositivo es esto se dice donde hace
 * falta: en la pantalla de vinculación (`TabletPinScreen`).
 */
function KitFrame({
  style,
  chrome,
  wide,
  children,
}: {
  style: CSSProperties
  chrome: boolean
  wide: boolean
  children: ReactNode
}) {
  return (
    <div
      style={style}
      className="flex min-h-screen flex-col bg-gradient-to-b from-surface to-surface-elevated"
    >
      <div className="flex flex-1 flex-col items-center justify-center px-lg py-xl">
        <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-xl'}`}>{children}</div>
      </div>
      {chrome && <AboutButton />}
    </div>
  )
}

/**
 * Puente entre un cuestionario y el siguiente: sin esto el jugador ve por un
 * instante el formulario que acaba de enviar (el Outlet sigue montado
 * mientras se resuelve `/auth/me`) y cree que no se guardó.
 */
function ChainingScreen() {
  return <StatusScreen title="Respuestas guardadas" detail="Buscando si te queda algo más…" />
}

/** Panel de espera del modo tablet: un título y una línea, nada tocable. */
function StatusScreen({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="panel max-w-xl p-2xl text-center" role="status">
      <h1 className="m-0 text-2xl">{title}</h1>
      <p className="mt-md text-lg text-text-secondary">{detail}</p>
    </div>
  )
}

function ThanksScreen({
  displayName,
  onDone,
}: {
  displayName: string | null
  onDone: () => void
}) {
  const first = displayName?.split(' ')[0] ?? null
  return (
    <TabletDoneScreen
      title={first ? `¡Gracias, ${first}!` : '¡Gracias!'}
      lead="Guardamos tus respuestas."
      onDone={onDone}
    />
  )
}

/**
 * Los pasos que el jugador tiene que dar DESPUÉS de soltar la tablet, en
 * orden: primero los sensores (se ponen antes de entrar al VR, según
 * `vrs-docs/34-manual-kit-fisiologico.md` §2), después el visor.
 *
 * Cada línea describe algo que el sistema realmente hace:
 *
 *  - Banda de pecho (Polar H10): colocación y humedecido de los electrodos
 *    salen de `vrs-docs/34-manual-kit-fisiologico.md` §2 ("Colocación de los
 *    sensores") y §3 (banda floja o electrodos secos = sin HR). Sin GSR ni
 *    base desde 2026-09-28 (setup de club: tablet, Polar y Quest). El Polar
 *    lo engancha solo la app VerSports Sensores del visor.
 *  - Código de 6 dígitos: el mismo del pad de la tablet — `/auth/kit/login`
 *    es el mismo endpoint en las dos superficies (`vr-app/src/ui/KitLogin.tsx`).
 *  - Sensores: se verifican dentro de GateSequence, después de Comenzar.
 *  - "Comenzar en VR" es el rótulo literal del botón del lobby
 *    (`vr-app/src/App.tsx`). El chequeo previo ya se resolvió en esta tablet.
 */
export { PLAYER_SETUP_STEPS as DONE_STEPS } from '../setup/playerSetupSteps'

/**
 * Cierre del turno de un jugador en la tablet — la usan las dos salidas
 * ("¡Gracias!" post-cuestionario y "Listo" cuando no había nada pendiente).
 * En los dos casos el jugador sigue hacia el visor, así que la guía es la
 * misma y sólo cambia el encabezado.
 *
 * Es la ÚLTIMA pantalla que ve antes de levantarse, y hasta el 2026-09-10 era
 * una sola línea ("pedile el visor al staff"): decía a quién pedirle el
 * aparato pero no qué hacer con los sensores ni cómo arrancar. mg: *"la
 * tablet misma al terminar te tiene que guiar: que al terminar te diga que te
 * pongas los sensores y cómo, y que te pongas el VR y cómo iniciar para
 * arrancar"*. De ahí los pasos numerados y la ilustración de dónde va cada
 * sensor.
 *
 * No se cierra sola: tenía un reloj de 90 s que la devolvía al PIN aunque
 * nadie hubiese terminado de leer, y mg (2026-09-18) pidió sacarlo — la
 * pantalla se cierra solo cuando el jugador toca "Listo". El watchdog de
 * inactividad (`useIdleLogout`, 4 min) sigue como red por si alguien se va
 * sin tocar nada.
 */
export function TabletDoneScreen({
  title,
  lead,
  onDone,
}: {
  title: string
  lead?: string
  onDone: () => void
}) {
  const [stepIndex, setStepIndex] = useState(0)
  const step = DONE_STEPS[stepIndex]!
  const last = stepIndex === DONE_STEPS.length - 1
  return (
    <div className="panel max-w-2xl p-2xl">
      <h1 className="m-0 text-center text-2xl">{title}</h1>
      {lead && <p className="mt-md text-center text-lg text-text-secondary">{lead}</p>}

      <section className="mt-xl text-center" aria-live="polite">
        <p className="m-0 text-sm font-semibold text-text-muted">Paso {stepIndex + 1} de {DONE_STEPS.length}</p>
        {step.image ? <img src={step.image} alt={step.alt} className="mx-auto my-lg h-48 max-w-full object-contain" /> : <div className="mx-auto my-lg"><RightControllerIllustration /></div>}
        <h2 className="m-0 text-xl">{step.title}</h2>
        <p className="mx-auto mt-md max-w-xl text-lg text-text-secondary">{step.detail}</p>
      </section>

      <p className="mt-lg text-center text-sm">
        <a href="/guia" target="_blank" rel="noopener noreferrer" className="underline">Guía completa (abre otra pestaña)</a>
      </p>
      <div className="mt-xl flex gap-md">
        <button type="button" className="btn flex-1 py-lg text-lg" disabled={stepIndex === 0} onClick={() => setStepIndex((n) => n - 1)}>
          Atrás
        </button>
        <button type="button" className="btn-primary flex-1 py-lg text-lg" onClick={() => last ? onDone() : setStepIndex((n) => n + 1)}>
          {last ? 'Listo' : 'Siguiente'}
        </button>
      </div>
    </div>
  )
}
